import React, { useEffect, useState, useRef } from "react";
import {
  Container, Typography, Box, Paper, Slider,
  FormControlLabel, Switch, Button, useMediaQuery,
  FormControl, InputLabel, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import AuthHeader from "../components/AuthHeader";

function SentenceViewerPage({ API_BASE, AUDIO_BASE }) {
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const searchParams = new URLSearchParams(location.search);
  const weekNumParam = searchParams.get("week_num");
  const hasWeekFilter = Boolean(weekNumParam);

  const [sentences, setSentences] = useState([]);
  const [weekOptions, setWeekOptions] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(weekNumParam || "");
  const [source, setSource] = useState("recognized");
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [loopAll, setLoopAll] = useState(false);
  const [autoNext, setAutoNext] = useState(false);
  const [showEnglish, setShowEnglish] = useState(true);
  const [individualEnglishShow, setIndividualEnglishShow] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [playingKey, setPlayingKey] = useState(null);
  const audioRefs = useRef({});
  const sentenceRefs = useRef({});
  const isPlayingRef = useRef(false);
  const [autoNextTimer, setAutoNextTimer] = useState(null);

  // 音声再生ログ関連
  const [currentPlayLog, setCurrentPlayLog] = useState(null);

  // ログイン促進ダイアログ関連
  const [showLoginDialog, setShowLoginDialog] = useState(false);

  // 音声再生ログ開始
  const startAudioPlayLog = async (sentence, audioFileName) => {
    // db_user_idが無い場合はログアウトしてログイン画面へリダイレクト
    if (!user?.db_user_id) {
      console.warn('⚠️ db_user_idが無いため、ログアウトしてログイン画面へリダイレクトします。');
      logout();
      return;
    }

    try {
      // audio_filenameからtranscribed_sentences.idを取得
      const idResponse = await fetch(`${API_BASE}/transcribed_sentence_id_by_audio/${audioFileName}`);
      const idData = await idResponse.json();
      const transcribedId = idData.id;

      const logData = {
        session_id: String(user.db_user_id),
        content_type: 'sentence',
        content_id: transcribedId || sentence.id, // transcribed_sentences.idを優先、なければsentence.id
        audio_file_name: audioFileName,
        is_video: 0, // 音声の場合は0
        play_start_time: new Date().toISOString(),
        playback_rate: playbackRate
      };

      const response = await fetch(`${API_BASE}/audio_play_logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logData)
      });

      const data = await response.json();
      if (data.success) {
        setCurrentPlayLog({
          log_id: data.log_id,
          start_time: new Date(),
          ...logData
        });
        console.log('★音声再生ログ開始:', data.log_id);
      }
    } catch (error) {
      console.error('★音声ログ開始エラー:', error);
    }
  };

  // 音声再生ログ完了
  const completeCurrentPlayLog = async () => {
    if (!currentPlayLog) return;

    try {
      const endTime = new Date();
      const durationSeconds = (endTime - currentPlayLog.start_time) / 1000;

      const updateData = {
        ...currentPlayLog,
        play_end_time: endTime.toISOString(),
        duration_seconds: durationSeconds
      };

      await fetch(`${API_BASE}/audio_play_logs/${currentPlayLog.log_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });

      console.log('★音声再生ログ完了:', durationSeconds, '秒');
      setCurrentPlayLog(null);
    } catch (error) {
      console.error('★音声ログ完了エラー:', error);
    }
  };

  // ログイン状態チェック
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/sentence_viewer' }, replace: true });
    }
  }, [isAuthenticated, navigate]);

  // 既存のuseEffect群...
  useEffect(() => {
    if (hasWeekFilter) {
      setSelectedWeek(weekNumParam);
    } else {
      fetch(`${API_BASE}/transcription_weeks`)
        .then((res) => res.json())
        .then((data) => {
          const filtered = Array.isArray(data)
            ? data.filter((w) => w.category === "ExampleSent").map((w) => w.week_num)
            : [];
          const reversed = [...filtered].reverse();
          setWeekOptions(reversed);
          if (reversed.length > 0) {
            setSelectedWeek(reversed[0]);
          }
        })
        .catch((err) => console.error("week fetch error:", err));
    }
  }, []);

  useEffect(() => {
    if (selectedWeek) {
      const endpoint = hasWeekFilter
        ? `transcribed_sentences_by_week/${selectedWeek}`
        : (source === "recognized"
          ? `recognized_sentences_by_week/${selectedWeek}`
          : `transcribed_sentences_by_week/${selectedWeek}`);

      fetch(`${API_BASE}/${endpoint}`)
        .then((res) => res.json())
        .then((data) => {
          const sentenceData = Array.isArray(data) ? data : [];
          setSentences(sentenceData);
          setIndividualEnglishShow(new Array(sentenceData.length).fill(true));
        })
        .catch((err) => console.error("API fetch error:", err));
    }
  }, [selectedWeek, source, hasWeekFilter]);

  useEffect(() => {
    Object.values(audioRefs.current).forEach((audio) => {
      if (audio) audio.playbackRate = playbackRate;
    });
  }, [playbackRate, sentences]);

  useEffect(() => {
    if (currentIndex !== null && sentenceRefs.current[currentIndex]) {
      sentenceRefs.current[currentIndex].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [currentIndex]);

  const handleGlobalEnglishToggle = (checked) => {
    setShowEnglish(checked);
    setIndividualEnglishShow(new Array(sentences.length).fill(checked));
  };

  const handleIndividualEnglishToggle = (index, checked) => {
    const newIndividualShow = [...individualEnglishShow];
    newIndividualShow[index] = checked;
    setIndividualEnglishShow(newIndividualShow);
    
    const allTrue = newIndividualShow.every(show => show);
    const allFalse = newIndividualShow.every(show => !show);
    if (allTrue) {
      setShowEnglish(true);
    } else if (allFalse) {
      setShowEnglish(false);
    }
  };

  const stopAllAudio = async () => {
    // 現在の再生ログを完了
    if (currentPlayLog) {
      await completeCurrentPlayLog();
    }

    Object.values(audioRefs.current).forEach((audio) => {
      if (audio && !audio.paused) {
        audio.pause();
        audio.currentTime = 0;
      }
    });
    if (autoNextTimer) {
      clearTimeout(autoNextTimer);
      setAutoNextTimer(null);
    }
    isPlayingRef.current = false;
    setCurrentIndex(null);
    setPlayingKey(null);
  };

  const handlePlay = async (index, audioIndex = 0) => {
    await stopAllAudio();
    
    const key = `${index}_${audioIndex}`;
    const audio = audioRefs.current[key];
    const currentSentence = sentences[index];
    
    if (audio && currentSentence) {
      audio.play();
      isPlayingRef.current = true;
      setCurrentIndex(index);
      setPlayingKey(key);
      
      // 音声ファイル名を取得
      let audioFileName = null;
      if (Array.isArray(currentSentence.audio_files) && currentSentence.audio_files[audioIndex]) {
        audioFileName = currentSentence.audio_files[audioIndex];
      }
      
      // 音声再生ログ開始
      if (audioFileName) {
        await startAudioPlayLog(currentSentence, audioFileName);
      }
    }
  };

  const handleEnded = async () => {
    // 音声終了時の処理
    if (currentPlayLog) {
      await completeCurrentPlayLog();
    }

    if (!isPlayingRef.current || !playingKey) return;
    
    const [indexStr, audioIndexStr] = playingKey.split("_");
    const index = parseInt(indexStr);
    const audioIndex = parseInt(audioIndexStr);
    const currentItem = sentences[index];
    const nextAudioIndex = audioIndex + 1;

    if (Array.isArray(currentItem.audio_files) && nextAudioIndex < currentItem.audio_files.length) {
      if (autoNext) {
        const timer = setTimeout(() => handlePlay(index, nextAudioIndex), 1000);
        setAutoNextTimer(timer);
      } else {
        // 既にcompleteCurrentPlayLog()を呼んだので、stopAllAudioは呼ばずに直接状態をリセット
        isPlayingRef.current = false;
        setCurrentIndex(null);
        setPlayingKey(null);
      }
    } else {
      const nextIndex = index + 1;
      if (autoNext && nextIndex < sentences.length) {
        const timer = setTimeout(() => handlePlay(nextIndex, 0), 1000);
        setAutoNextTimer(timer);
      } else if (loopAll) {
        const timer = setTimeout(() => handlePlay(index, 0), 1000);
        setAutoNextTimer(timer);
      } else {
        // 既にcompleteCurrentPlayLog()を呼んだので、stopAllAudioは呼ばずに直接状態をリセット
        isPlayingRef.current = false;
        setCurrentIndex(null);
        setPlayingKey(null);
      }
    }
  };


  const isRecognizedSentences = source === "recognized";

  // ログイン中のみ表示
  if (!isAuthenticated) {
    return null; // リダイレクト中は何も表示しない
  }

  return (
    <>
      <AuthHeader />
      <Container maxWidth="md" sx={{ paddingBottom: 24 }}>

      {!hasWeekFilter && (
        <>
          <Box sx={{ marginBottom: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="source-label">表示元</InputLabel>
              <Select
                labelId="source-label"
                value={source}
                label="表示元"
                onChange={(e) => setSource(e.target.value)}
              >
                <MenuItem value="recognized">認識済み（recognized_sentences）</MenuItem>
                <MenuItem value="transcribed">書き起こし（transcribed_sentences）</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ marginBottom: 4 }}>
            <FormControl fullWidth>
              <InputLabel id="week-label">週番号を選択</InputLabel>
              <Select
                labelId="week-label"
                value={selectedWeek}
                label="週番号を選択"
                onChange={(e) => setSelectedWeek(e.target.value)}
              >
                {weekOptions.map((week, i) => (
                  <MenuItem key={i} value={week}>
                    {week}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </>
      )}

      <Box sx={{ marginBottom: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Switch 
              checked={showEnglish}
              onChange={(e) => handleGlobalEnglishToggle(e.target.checked)}
            />
          }
          label="全て英語表示"
        />
      </Box>

      <Box display="flex" flexDirection="column" gap={3}>
        {Array.isArray(sentences) &&
          sentences.map((item, index) => (
            <Paper
              key={item.id || index}
              ref={(el) => (sentenceRefs.current[index] = el)}
              sx={{
                padding: 2,
                backgroundColor: currentIndex === index ? "#e3f2fd" : "inherit",
              }}
            >
              {isRecognizedSentences ? (
                <>
                  {item.sentence_jp && (
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      {item.sentence_jp}
                    </Typography>
                  )}
                  
                  <Box sx={{ marginBottom: 2 }}>
                    <Typography 
                      variant="h6" 
                      gutterBottom
                      sx={{ 
                        visibility: individualEnglishShow[index] ? 'visible' : 'hidden',
                        minHeight: '1.5rem',
                        lineHeight: 1.5
                      }}
                    >
                      {item.sentence || ' '}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <FormControlLabel
                      control={
                        <Switch 
                          size="small"
                          checked={individualEnglishShow[index] || false}
                          onChange={(e) => handleIndividualEnglishToggle(index, e.target.checked)}
                        />
                      }
                      label="英語"
                      sx={{ marginRight: 2 }}
                    />
                    
                    {Array.isArray(item.audio_files) && item.audio_files.map((file, i) => (
                      <React.Fragment key={`${index}_${i}`}>
                        {playingKey === `${index}_${i}` ? (
                          <Button variant="outlined" color="error" onClick={stopAllAudio} sx={{ mr: 1 }}>
                            停止
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            onClick={() => handlePlay(index, i)}
                            sx={{ mr: 1 }}
                          >
                            再生
                          </Button>
                        )}
                        <audio
                          style={{ display: "none" }}
                          ref={(el) => (audioRefs.current[`${index}_${i}`] = el)}
                          onEnded={handleEnded}
                        >
                          <source src={`${AUDIO_BASE}/audio_clipped/${file}`} type="audio/mpeg" />
                        </audio>
                      </React.Fragment>
                    ))}
                  </Box>
                </>
              ) : (
                <>
                  {item.sentence_jp && (
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      {item.sentence_jp}
                    </Typography>
                  )}
                  
                  <Box sx={{ marginBottom: 2 }}>
                    <Typography 
                      variant="h6" 
                      gutterBottom
                      sx={{ 
                        visibility: individualEnglishShow[index] ? 'visible' : 'hidden',
                        minHeight: '1.5rem',
                        lineHeight: 1.5
                      }}
                    >
                      {item.sentence || ' '}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <FormControlLabel
                      control={
                        <Switch 
                          size="small"
                          checked={individualEnglishShow[index] || false}
                          onChange={(e) => handleIndividualEnglishToggle(index, e.target.checked)}
                        />
                      }
                      label="英語"
                      sx={{ marginRight: 2 }}
                    />

                    {Array.isArray(item.audio_files) && item.audio_files.map((file, i) => (
                      <React.Fragment key={`${index}_${i}`}>
                        {playingKey === `${index}_${i}` ? (
                          <Button variant="outlined" color="error" onClick={stopAllAudio} sx={{ mr: 1 }}>
                            停止
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            onClick={() => handlePlay(index, i)}
                            sx={{ mr: 1 }}
                          >
                            再生
                          </Button>
                        )}
                        <audio
                          style={{ display: "none" }}
                          ref={(el) => (audioRefs.current[`${index}_${i}`] = el)}
                          onEnded={handleEnded}
                        >
                          <source src={`${AUDIO_BASE}/audio_clipped/${file}`} type="audio/mpeg" />
                        </audio>
                      </React.Fragment>
                    ))}
                  </Box>
                </>
              )}
            </Paper>
          ))}
      </Box>

      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          backgroundColor: "#fff",
          borderTop: "1px solid #ccc",
          padding: 2,
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          gap: 2,
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000,
        }}
      >
        <Box minWidth={200}>
          <Typography gutterBottom>再生速度：{playbackRate.toFixed(1)}x</Typography>
          <Slider
            value={playbackRate}
            onChange={(_, val) => {
              // テストユーザーの場合、1.0以外への変更時にログインダイアログを表示
              if (user?.id === 'test-user-123' && val !== 1.0) {
                setShowLoginDialog(true);
                return;
              }
              setPlaybackRate(val);
            }}
            min={0.5}
            max={1.5}
            step={0.1}
            valueLabelDisplay="auto"
            marks={[{ value: 0.5, label: "0.5x" }, { value: 1.0, label: "1.0x" }, { value: 1.5, label: "1.5x" }]}
          />
        </Box>
        <Box display="flex" flexDirection="row" gap={4}>
          <FormControlLabel
            control={<Switch checked={loopAll} onChange={(e) => setLoopAll(e.target.checked)} />}
            label="ループ再生"
          />
          <FormControlLabel
            control={<Switch checked={autoNext} onChange={(e) => setAutoNext(e.target.checked)} />}
            label="連続再生"
          />
        </Box>
      </Box>

      {/* ログイン促進ダイアログ */}
      <Dialog
        open={showLoginDialog}
        onClose={() => setShowLoginDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>ログインが必要です</DialogTitle>
        <DialogContent>
          <Typography>
            再生速度の変更機能を利用するには、Googleアカウントでログインしてください。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLoginDialog(false)} color="primary">
            キャンセル
          </Button>
          <Button 
            onClick={() => {
              setShowLoginDialog(false);
              // ローカルストレージをクリアしてからページをリロード
              localStorage.removeItem('auth_token');
              localStorage.removeItem('user_info');
              window.location.reload();
            }} 
            color="primary" 
            variant="contained"
          >
            ログインする
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </>
  );
}

export default SentenceViewerPage;